import { EPD_EXPIRING_SOON_DAYS, getEpdValidityStatus } from "@/services/Utils/epdValidity";
import type { Material } from "@/types/Views/MaterialDatabase/Material";
import type { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

import type { ColourKey, ColourKeyCategory } from "./colourKeys";
import { layerCountSubLine } from "./keyLayers";
import { EPD_QUALITY_COLOURS, EPD_QUALITY_SWATCH_CLASSES } from "./epdQuality";

export type EpdValidity = "expired" | "expiringSoon" | "undated" | "valid" | "unmapped";

// Worst first, Not mapped last: a type takes the earliest category any layer has, so an unmapped layer never hides an expired one.
export const EPD_VALIDITY_WORST_FIRST: EpdValidity[] = ["expired", "expiringSoon", "undated", "valid", "unmapped"];

// Scene colours, mirroring the epd-validity-* tokens in style.css; one shade off the mapping tool's green/yellow/red.
export const EPD_VALIDITY_COLOURS: Record<EpdValidity, string> = {
  expired: "#b81f1d",
  expiringSoon: "#f5be2a",
  undated: "#bb99e7",
  valid: "#508b25",
  unmapped: EPD_QUALITY_COLOURS.unmapped,
};

export const EPD_VALIDITY_SWATCH_CLASSES: Record<EpdValidity, string> = {
  expired: "bg-epd-validity-expired",
  expiringSoon: "bg-epd-validity-expiring",
  undated: "bg-epd-validity-undated",
  valid: "bg-epd-validity-valid",
  unmapped: EPD_QUALITY_SWATCH_CLASSES.unmapped,
};

export const EPD_VALIDITY_LABELS: Record<EpdValidity, string> = {
  expired: "Expired",
  expiringSoon: "Expiring soon",
  undated: "No expiry date",
  valid: "Valid",
  unmapped: "Not mapped",
};

// A mapped record without a parseable valid-to date is its own category; the shared helper would read it as valid.
export function layerValidity(mapping: Material | null | undefined): EpdValidity {
  if (!mapping) return "unmapped";
  const validTo = mapping.epdInfo?.validTo;
  if (!validTo || Number.isNaN(new Date(validTo).getTime())) return "undated";
  return getEpdValidityStatus(validTo);
}

const EPD_VALIDITY_CATEGORIES: ColourKeyCategory[] = EPD_VALIDITY_WORST_FIRST.map((validity) => ({
  id: validity,
  label: EPD_VALIDITY_LABELS[validity],
  colour: EPD_VALIDITY_COLOURS[validity],
  swatchClass: EPD_VALIDITY_SWATCH_CLASSES[validity],
}));

const SUB_LINE_WORDING: Record<string, string> = {
  expired: "expired",
  expiringSoon: "expiring soon",
  undated: "without an expiry date",
  valid: "on a valid EPD",
  unmapped: "mapped",
};

export function validitySubLine(type: SpeckleMaterial, category: string): string {
  return layerCountSubLine(type, category, (layer) => layerValidity(layer.mapping), SUB_LINE_WORDING);
}

export const EPD_VALIDITY_KEY = {
  id: "epdValidity" as const,
  label: "EPD validity",
  group: "epd",
  hint: `Each type shows the worst validity among its material layers; soon means within ${Math.round(
    EPD_EXPIRING_SOON_DAYS / 30
  )} months.`,
  categoriesFor: () => EPD_VALIDITY_CATEGORIES,
  trackCategory: "unmapped",
  headline: {
    goodCategories: ["valid"],
    text: (good: number, total: number) => `${good} / ${total} elements on a valid EPD`,
  },
  classifyLayer: (layer: SpeckleMaterial) => layerValidity(layer.mapping),
  subLine: validitySubLine,
} satisfies ColourKey;
